import React, { useState, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { Menu } from "react-native-paper";
import { LinearGradient } from "expo-linear-gradient";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import Pill from "../../components/common/Pill/Pill";
import { Plus } from "@phosphor-icons/react/dist/csr/Plus";
import { CaretDown } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUp } from "@phosphor-icons/react/dist/csr/CaretUp";
import { GestureResponderEvent } from "react-native";

export enum AdminUsersModalState {
  CLOSED = "CLOSED",
  REVOKE_FACULTY = "REVOKE_FACULTY",
  REVOKE_CONFIRMATION = "REVOKE_CONFIRMATION",
  ASSIGN_COURSE = "ASSIGN_COURSE",
  REMOVE_COURSE = "REMOVE_COURSE",
  INVITE = "INVITE",
  RESEND_INVITE = "RESEND_INVITE",
}

interface User {
  id: string;
  name: string;
  email: string;
  lastActive: string;
  hasRoleRoles: { name: string }[];
  teachingCourses: { name: string; course_code?: string | null }[];
  pendingFacultyInvite: boolean;
  expiredInvitation: boolean;
  hasSessionDeviceSessions: { id: string }[];
}

interface AdminUsersTableProps {
  users: User[];
  onActionModalOpen: (userId: string, userName: string, modalType: AdminUsersModalState) => void;
  resendInvite: ({
    email,
    name,
    role,
  }: {
    email: string;
    name: string;
    role: "Faculty" | "School Admin" | "Auditor";
  }) => Promise<boolean>;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  userId: string;
  userName: string;
  userEmail: string;
  courseCount: number;
  userRole: string | null;
  isPendingInvite: boolean;
  isExpiredInvite: boolean;
};

const AdminUsersTable: React.FC<AdminUsersTableProps> = ({ users, onActionModalOpen, resendInvite }) => {
  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    userId: null,
    userName: null,
    userEmail: null,
    courseCount: 0,
    userRole: null,
    isPendingInvite: false,
    isExpiredInvite: false,
  });

  const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
    if (courseCode && courseCode.trim()) {
      return `${courseCode} ${courseName}`;
    }
    return courseName;
  };

  const isAdminUser = (roles: { name: string }[]): boolean => {
    return roles.some((role) => role.name === "School Admin" || role.name === "Admin");
  };

  const canHaveCoursesAssigned = (roles: { name: string }[]): boolean => {
    return roles.some((role) => role.name === "Faculty" || role.name === "School Admin" || role.name === "Admin");
  };

  const getDisplayRole = (roles: { name: string }[]): string => {
    // Check if user is admin first (School Admin or Admin)
    if (isAdminUser(roles)) {
      return "Admin";
    }

    if (roles.some((role) => role.name === "Faculty")) {
      return "Faculty";
    }

    return roles
      .map((role) => {
        if (role.name === "User") {
          return "Student";
        }
        return role.name;
      })
      .join(", ");
  };

  const handleOpenMenu = useCallback((event: GestureResponderEvent, user: User) => {
    const { pageX, pageY } = event.nativeEvent;

    setMenuState({
      visible: true,
      x: pageX - 100,
      y: pageY,
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      courseCount: user.teachingCourses.length,
      userRole: user.hasRoleRoles[0]?.name || null,
      isPendingInvite: user.pendingFacultyInvite,
      isExpiredInvite: user.expiredInvitation,
    });
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState({
      visible: false,
      userId: null,
      userName: null,
      userEmail: null,
      courseCount: 0,
      userRole: null,
      isPendingInvite: false,
      isExpiredInvite: false,
      x: 0,
      y: 0,
    });
  }, []);

  const handleActionModalOpen = (modalType: AdminUsersModalState) => {
    handleCloseMenu();
    if (modalType === AdminUsersModalState.RESEND_INVITE) {
      // Handle resend invite directly
      resendInvite({
        email: menuState.userEmail,
        name: menuState.userName,
        role: "Faculty",
      });
    } else {
      onActionModalOpen(menuState.userId, menuState.userName, modalType);
    }
  };

  const handleAssignCourseClick = (user: User) => {
    onActionModalOpen(user.id, user.name, AdminUsersModalState.ASSIGN_COURSE);
  };

  return (
    <>
      <ScrollView style={styles.tableContainer}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.nameCell]}>
              <CustomText text="Name" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.teachingCell]}>
              <CustomText text="Teaching" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.roleCell]}>
              <CustomText text="Role" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.actionsCell]}>
              <CustomText text="Actions" size="s" weight="medium" style={styles.headerText} />
            </View>
          </View>

          {/* Table Body */}
          {users.map((user) => {
            const isMenuOpen = menuState.visible && menuState.userId === user.id;
            const initials = user.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .substring(0, 2);

            const userCanHaveCourses = canHaveCoursesAssigned(user.hasRoleRoles);
            const displayRole = getDisplayRole(user.hasRoleRoles);

            return (
              <View key={user.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.nameCell]}>
                  <View style={styles.nameContent}>
                    {user.pendingFacultyInvite ? (
                      <View style={styles.avatar}>
                        <CustomText
                          text={initials}
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={styles.avatarText}
                        />
                      </View>
                    ) : (
                      <LinearGradient
                        colors={["#3E68FE", "#913DAF"]}
                        style={styles.avatar}
                        start={{ x: 0, y: 0.5 }}
                        end={{ x: 1, y: 0.5 }}>
                        <CustomText
                          text={initials}
                          size="s"
                          weight="medium"
                          useTranslationText={false}
                          style={[styles.avatarText, styles.activeAvatarText]}
                        />
                      </LinearGradient>
                    )}
                    <View style={styles.nameInfo}>
                      <CustomText
                        text={user.name}
                        size="s"
                        weight="medium"
                        useTranslationText={false}
                        style={styles.nameText}
                      />
                      <CustomText text={user.email} size="xs" style={styles.emailText} useTranslationText={false} />
                    </View>
                  </View>
                </View>

                <View style={[styles.cell, styles.teachingCell]}>
                  {!userCanHaveCourses ? (
                    <CustomText text="Not Available" size="s" style={styles.pendingText} />
                  ) : user.pendingFacultyInvite || user.expiredInvitation ? (
                    <CustomText
                      text={user.expiredInvitation ? "Expired Invitation" : "Pending Confirmation"}
                      size="s"
                      style={user.expiredInvitation ? styles.expiredText : styles.pendingText}
                    />
                  ) : user.teachingCourses.length === 0 ? (
                    <CustomButton
                      text="Assign Course"
                      size="s"
                      leftIcon={<Plus size={14} color="#344054" />}
                      onPress={() => handleAssignCourseClick(user)}
                      useTranslationText={false}
                      style={styles.assignButton}
                      textStyle={styles.assignButtonText}
                    />
                  ) : (
                    <View style={styles.coursesContainer}>
                      {user.teachingCourses.map((course, index) => (
                        <Pill
                          key={index}
                          text={formatCourseNameWithCode(course.name, course.course_code)}
                          leftIcon={null}
                          backgroundColor="#E0F2FE"
                          textColor="#0369A1"
                        />
                      ))}
                    </View>
                  )}
                </View>

                <View style={[styles.cell, styles.roleCell]}>
                  <CustomText text={displayRole} size="s" style={styles.roleText} useTranslationText={false} />
                </View>

                <View style={[styles.cell, styles.actionsCell]}>
                  <CustomButton
                    text="Manage"
                    size="s"
                    rightIcon={
                      isMenuOpen ? <CaretUp size={14} color="#FFFFFF" /> : <CaretDown size={14} color="#475569" />
                    }
                    onPress={(e) => handleOpenMenu(e, user)}
                    useTranslationText={false}
                    styleType="transparent"
                    style={[styles.manageButton, isMenuOpen && styles.manageButtonActive]}
                    textStyle={[styles.manageText, isMenuOpen && styles.manageTextActive]}
                  />
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      <Menu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        contentStyle={styles.menu}>
        {(menuState.isPendingInvite || menuState.isExpiredInvite) && (
          <Menu.Item
            onPress={() => handleActionModalOpen(AdminUsersModalState.RESEND_INVITE)}
            title="Resend Invite"
            titleStyle={[styles.menuItemText, { color: "#3E68FE" }]}
          />
        )}
        <Menu.Item
          disabled={menuState.userRole === "School Admin" || menuState.userRole === "Admin"}
          onPress={() => handleActionModalOpen(AdminUsersModalState.REVOKE_FACULTY)}
          title="Revoke Faculty"
          titleStyle={[
            styles.menuItemText,
            { color: menuState.userRole === "School Admin" || menuState.userRole === "Admin" ? "#6B7280" : "#DC2626" },
          ]}
        />
        <Menu.Item
          disabled={menuState.courseCount === 0}
          onPress={() => handleActionModalOpen(AdminUsersModalState.REMOVE_COURSE)}
          title="Remove from Course"
          titleStyle={[styles.menuItemText, { color: menuState.courseCount === 0 ? "#6B7280" : "#3E68FE" }]}
        />
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  nameCell: {
    flex: 3,
  },
  teachingCell: {
    flex: 3,
    alignItems: "flex-start",
  },
  roleCell: {
    flex: 1.5,
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "center",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  nameContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#E0E0E0",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#666",
    fontSize: 16,
  },
  activeAvatarText: {
    color: "#FFFFFF",
  },
  nameInfo: {
    flex: 1,
  },
  nameText: {
    color: "#111827",
    fontSize: 15,
  },
  emailText: {
    color: "#6B7280",
    marginTop: 2,
  },
  pendingText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  expiredText: {
    color: "#DC2626",
    fontStyle: "italic",
  },
  assignButton: {
    backgroundColor: "#F9FAFB",
    borderColor: "#EAECF0",
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  assignButtonText: {
    fontSize: 13,
    color: "#475569",
  },
  coursesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
  },
  roleText: {
    color: "#101828",
  },
  manageButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
  manageButtonActive: {
    backgroundColor: "#3E68FE",
    borderColor: "#3E68FE",
  },
  manageText: {
    color: "#475569",
    fontSize: 13,
  },
  manageTextActive: {
    color: "#FFFFFF",
  },
  menu: {
    backgroundColor: "#FFFFFF",
    minWidth: 200,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
});

export default AdminUsersTable;
